import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CalendarDays, Clock } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { BLOG_POSTS, getBlogPost, type BlogPost } from "@/lib/blog";
import { SITE_ORIGIN, ogUrl } from "@/lib/ogSections";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getBlogPost(params.slug);
    if (!post) throw notFound();
    return post;
  },
  component: BlogPostPage,
  head: ({ loaderData }: { loaderData?: BlogPost }) => {
    if (!loaderData) return { meta: [{ title: "Article not found | Arif Khan" }, { name: "robots", content: "noindex" }] };
    const url = `${SITE_ORIGIN}/blog/${loaderData.slug}`;
    const image = ogUrl(loaderData.image);
    return {
      meta: [
        { title: loaderData.metaTitle },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: loaderData.metaTitle },
        { property: "og:description", content: loaderData.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1216" },
        { property: "og:image:height", content: "640" },
        { property: "og:image:alt", content: loaderData.title },
        { property: "article:section", content: loaderData.category },
        { property: "article:published_time", content: loaderData.publishedAt },
        { property: "article:modified_time", content: loaderData.updatedAt },
        { property: "article:author", content: "Arif Khan" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loaderData.metaTitle },
        { name: "twitter:description", content: loaderData.description },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: loaderData.title },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/blog/${loaderData.slug}`)],
      scripts: [
        pageSchemaScript(
          {
            path: `/blog/${loaderData.slug}`,
            type: "WebPage",
            name: loaderData.metaTitle,
            description: loaderData.description,
            breadcrumb: [
              { name: "Blog", url: "/blog" },
              { name: loaderData.title, url: `/blog/${loaderData.slug}` },
            ],
            extra: { mainEntity: { "@id": `${url}#article` } },
          },
          [
            {
              "@type": "BlogPosting",
              "@id": `${url}#article`,
              headline: loaderData.title,
              name: loaderData.title,
              description: loaderData.description,
              abstract: loaderData.excerpt,
              url,
              mainEntityOfPage: { "@id": `${url}#webpage` },
              image,
              datePublished: loaderData.publishedAt,
              dateModified: loaderData.updatedAt,
              inLanguage: "en-IN",
              author: { "@id": PERSON_ID },
              publisher: { "@id": PERSON_ID },
              about: { "@type": "Thing", name: loaderData.category },
              keywords: loaderData.category,
            },
            ...(loaderData.faqs?.length
              ? [
                  {
                    "@type": "FAQPage",
                    "@id": `${url}#faq`,
                    inLanguage: "en-IN",
                    isPartOf: { "@id": `${url}#webpage` },
                    mainEntity: loaderData.faqs.map((faq) => ({
                      "@type": "Question",
                      name: faq.question,
                      acceptedAnswer: { "@type": "Answer", text: faq.answer },
                    })),
                  },
                ]
              : []),
          ],
        ),
      ],
    };
  },
});

function BlogPostPage() {
  const post = Route.useLoaderData() as BlogPost;
  const related = BLOG_POSTS.filter((candidate) => candidate.slug !== post.slug);

  return (
    <PageShell>
      <article>
        <header className="border-b border-border bg-surface pb-16 pt-36">
          <div className="container-page max-w-4xl">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <ArrowLeft className="h-4 w-4" /> Back to Blog
            </Link>
            <div className="mt-12 flex flex-wrap items-center gap-5 text-sm text-ink-soft">
              <span className="eyebrow text-accent">{post.category}</span>
              <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {post.publishedAt}</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {post.readTime}</span>
            </div>
            <h1 className="mt-6 max-w-4xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-6xl">{post.title}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-soft">{post.excerpt}</p>
            <img src={post.image} alt={post.title} width={1216} height={640} loading="eager" className="mt-10 aspect-[16/9] w-full rounded-xl object-cover" />
            <p className="mt-6 text-sm text-ink-soft">By Arif Khan · Security Infrastructure & Building Technology Strategist</p>
          </div>
        </header>

        <div className="container-page grid gap-12 py-16 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="max-w-3xl">
            {post.shortAnswer && (
              <section id="short-answer" className="mb-12 border-l-2 border-accent pl-6">
                <p className="eyebrow text-accent">Short answer</p>
                <p className="mt-4 text-lg leading-relaxed">{post.shortAnswer}</p>
              </section>
            )}
            {post.sections.map((section) => (
              <section key={section.heading} className="mb-12">
                <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">{section.heading}</h2>
                <div className="mt-5 space-y-4 text-base leading-relaxed text-ink-soft">
                  {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                {section.bullets && (
                  <ul className="mt-6 list-disc space-y-3 pl-5 text-base leading-relaxed text-ink-soft">
                    {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                )}
              </section>
            ))}
            {post.faqs?.length ? (
              <section className="border-t border-border pt-10" aria-labelledby="faq-heading">
                <h2 id="faq-heading" className="text-2xl font-semibold tracking-tight md:text-3xl">Frequently asked questions</h2>
                <div className="mt-6 space-y-6">
                  {post.faqs.map((faq) => (
                    <div key={faq.question}>
                      <h3 className="text-lg font-semibold">{faq.question}</h3>
                      <p className="mt-2 leading-relaxed text-ink-soft">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="h-fit rounded-xl border border-border bg-surface p-6 lg:sticky lg:top-24">
            <p className="eyebrow">Continue exploring</p>
            <div className="mt-5 space-y-4">
              {post.related.map((item) => (
                <Link key={item.href} to={item.href as "/"} className="group flex items-start justify-between gap-3 text-sm font-medium leading-relaxed hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span>{item.label}</span><ArrowRight className="mt-1 h-4 w-4 shrink-0" />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </article>

      <section className="border-t border-border bg-surface py-20">
        <div className="container-page">
          <span className="eyebrow">More from the blog</span>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {related.map((item) => (
              <Link key={item.slug} to="/blog/$slug" params={{ slug: item.slug }} className="group rounded-xl border border-border bg-background p-6 hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="eyebrow text-accent">{item.category}</span>
                <h2 className="mt-3 text-xl font-semibold tracking-tight">{item.title}</h2>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium">Read next <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
